import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType, RequestState} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {IModalMessage, TModalArguments} from "data/types/modals";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {findIndex} from "lodash";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";

export interface ITeamSavedModalController extends ViewController {
	get i18n(): ILocalizationStore;
	get visibleModal(): ModalType | null;
	get modalContent(): IModalMessage | null;
	get selectedRoundIndex(): number;

	close: () => void;
	show: (...args: TModalArguments) => void;
	toNextRound: () => void;
}

@injectable()
export class TeamSavedModalController implements ITeamSavedModalController {
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get visibleModal() {
		return this._modalsStore.modal;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get selectedRoundIndex() {
		return findIndex(this._roundStore.list, (el) => el.id === this._roundStore.selectedRoundId);
	}

	show = (...args: TModalArguments) => {
		this._modalsStore.showModal(...args);
	};

	close = () => this._modalsStore.hideModal();

	private onChangeRound = (roundId: number) => {
		this._roundStore.setSelectedRoundId(roundId);
		this._roundStore.setSelectedRoundSessions();
		this._roundStore.setSelectedSessionId(this._roundStore.selectedRoundSessions[0].id ?? 0);
		void this._rankingStore.fetchGamebar({
			gamedayId: roundId,
		});
	};

	toNextRound = () => {
		const nextRound = this._roundStore.list[this.selectedRoundIndex + 1];
		this.onChangeRound(nextRound.id ?? 0);
		this.close();
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
